import { useQueryClient } from '@tanstack/react-query';
import { TimerOff } from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { type TimesheetState } from '@apm/contracts';
import { Button } from '../../components/ui/button';
import { timesheetApi, timesheetKeys } from './api';

/**
 * Hỏi trước khi một thao tác **làm dừng đồng hồ bấm giờ** — APM-TIME-done,
 * người dùng yêu cầu 2026-09-25: *"khi action mà làm dừng đếm giờ thì có
 * dialog thông báo và confirm"*.
 *
 * Việc đang bấm giờ mà thành **xong** (tiến độ 100%, Đóng việc, kéo sang cột
 * Hoàn thành) thì server tự dừng đồng hồ (`TaskDoneTimerStopper`). Hook này là
 * nửa giao diện của luật ấy: nếu đồng hồ **của chính người đang thao tác** đang
 * chạy trên đúng việc đó, hiện hộp nói rõ điều sắp xảy ra và chờ xác nhận;
 * không thì chạy luôn, không hỏi gì.
 *
 * Đọc trạng thái bấm giờ **từ server** (`staleTime: 0`), không từ cache: vừa
 * đặt 100% xong thì server đã dừng đồng hồ nhưng cache có thể chưa biết (tín
 * hiệu SSE chưa về) — đọc cache là hỏi thêm một lần ở bước "chuyển sang cột
 * Hoàn thành" ngay sau đó. Một request cho mỗi lần hoàn thành việc là rẻ. Đọc
 * hỏng thì **không chặn** thao tác: server vẫn tự dừng đồng hồ, hộp xác nhận
 * chỉ là lời báo trước.
 *
 * ```tsx
 * const stopConfirm = useTimerStopConfirm();
 * onProgress={(pct) => stopConfirm.guard(task.id, pct >= 100, () => save(pct))}
 * {stopConfirm.dialog}
 * ```
 */
export function useTimerStopConfirm(): {
  /**
   * `completes = false` thì chạy thẳng `run`. `onCancel` cho chỗ gọi cần hoàn
   * tác thứ đã vẽ lạc quan (thẻ vừa thả vào cột Hoàn thành).
   */
  readonly guard: (
    taskId: string,
    completes: boolean,
    run: () => void,
    onCancel?: () => void,
  ) => void;
  readonly dialog: ReactNode;
} {
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<{
    title: string;
    run: () => void;
    onCancel?: (() => void) | undefined;
  } | null>(null);

  const guard = useCallback(
    (taskId: string, completes: boolean, run: () => void, onCancel?: () => void): void => {
      if (!completes) {
        run();
        return;
      }
      void queryClient
        .fetchQuery<TimesheetState>({
          queryKey: timesheetKeys.state(),
          queryFn: timesheetApi.state,
          staleTime: 0,
        })
        .then(
          (state) => {
            const active = state.active;
            if (active?.target.kind === 'task' && active.target.id === taskId) {
              setPending({ title: active.target.title, run, onCancel });
            } else {
              run();
            }
          },
          () => run(),
        );
    },
    [queryClient],
  );

  const dialog = pending ? (
    <TimerStopConfirmDialog
      title={pending.title}
      onConfirm={() => {
        setPending(null);
        pending.run();
      }}
      onCancel={() => {
        setPending(null);
        pending.onCancel?.();
      }}
    />
  ) : null;

  return { guard, dialog };
}

function TimerStopConfirmDialog({
  title,
  onConfirm,
  onCancel,
}: {
  readonly title: string;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}): ReactNode {
  const { t } = useTranslation();

  /*
    Esc = Huỷ. Nghe ở pha **capture** và chặn lan truyền: popup chi tiết việc
    bên dưới cũng nghe Esc, và một lần Esc chỉ được đóng hộp xác nhận.
  */
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      onCancel();
    };
    window.addEventListener('keydown', onKey, { capture: true });
    return () => window.removeEventListener('keydown', onKey, { capture: true });
  }, [onCancel]);

  return createPortal(
    // `z-[60]`: cao hơn `z-50` của popup / drawer đang mở bên dưới.
    <div className="relative z-[60]">
      <div className="fixed inset-0 popup-scrim" onClick={onCancel} aria-hidden />
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="timer-stop-confirm-title"
        aria-describedby="timer-stop-confirm-body"
        className="fixed left-1/2 top-1/2 flex w-[calc(100%-2rem)] max-w-sm -translate-x-1/2 -translate-y-1/2 flex-col gap-3 rounded-DEFAULT border border-border bg-background p-4 shadow-xl glass-popup-sm on-scrim"
      >
        <div className="flex items-start gap-2">
          <TimerOff aria-hidden className="mt-0.5 size-4 shrink-0 text-rag-amber" />
          <h2 id="timer-stop-confirm-title" className="text-base font-semibold">
            {t('timesheet.stopConfirm.title')}
          </h2>
        </div>
        <p id="timer-stop-confirm-body" className="text-sm">
          {t('timesheet.stopConfirm.body', { title })}
        </p>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
          {/* `autoFocus`: Enter là xác nhận, Esc là Huỷ — không cần chuột. */}
          <Button type="button" onClick={onConfirm} autoFocus>
            {t('timesheet.stopConfirm.confirm')}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
